import { Injectable } from '@angular/core';
import { ITextPanelButton } from '@fi-shared/fi-text-panel/simple-text-panel/text-panel-base';
import { AppTextService } from '@fi-shared/app-text';
import {
    IAIAssistancePrompt,
    IAIRequestHistoryModel,
    IFiAIAssistanceResponseModel,
} from '@fi-shared/fi-ai-assistance/fi-ai-assistance';
import { BehaviorSubject, Observable } from 'rxjs';
import { isDefined } from '@fi-shared/utils';
import { TextEditorHistoryService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-history.service';
import { TextEditorInsertMediaService } from '@fi-shared/text-editor/text-editor-insert-media.service';
import { FiAIAssistanceService } from '@fi-shared/fi-ai-assistance/fi-ai-assistance.service';
import {
    getAiRequestEditorElement,
    getExistingAiRequestEditorElement,
    getExistingRequestInputEl,
    handleAiRequestInputChange,
    setClearRequestButtonVisibility,
    setPromptSampleContainerVisibility,
} from '@fi-shared/fi-ai-assistance/fi-ai-assistance-request-container';
import { FiAIAssistanceType } from '@fi-shared/fi-ai-assistance/fi-a-i-assistance-type';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';

@Injectable()
export class FiAIAssistanceHelperService {
    private _isAIAssistanceVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    setIsAIAssistanceVisible(value: boolean) {
        this._isAIAssistanceVisible$.next(value);
    }
    isAIAssistanceVisible$ = this._isAIAssistanceVisible$.asObservable();

    constructor(
        private appTextService: AppTextService,
        private textEditorHistoryService: TextEditorHistoryService,
        private textEditorInsertMediaService: TextEditorInsertMediaService,
        private fiAIAssistanceService: FiAIAssistanceService,
    ) {}

    getPanelButtons(): ITextPanelButton[] {
        return [
            {
                title: this.appTextService.get('text_editor.ai_assistance'),
                class: 'far fa-user-robot',
                btnClass: 'BrandedColorHover panel-icon-button',
                selected: () => {
                    return !isNullOrUndefined(getExistingAiRequestEditorElement());
                },
                callback: () => {
                    const fiAiAssistanceEditor: HTMLElement = getExistingAiRequestEditorElement();
                    if (!isNullOrUndefined(fiAiAssistanceEditor)) {
                        this.hideAiAssistanceEditor(fiAiAssistanceEditor);
                        return;
                    }
                    this.showAiRequestEditor(this.tempAiRequestText);
                },
            },
        ];
    }

    private promptSamples: IAIAssistancePrompt[];
    showAiRequestEditor(requestMessage?: string) {
        if (!isDefined(this.promptSamples)) {
            this.fiAIAssistanceService
                .getAIPromptSamples(FiAIAssistanceType.Document)
                .then((promptSamples: IAIAssistancePrompt[]) => {
                    this.promptSamples = promptSamples;
                    this.showAiRequestEditorInternal(requestMessage);
                });
        } else {
            this.showAiRequestEditorInternal(requestMessage);
        }
    }

    hideAiAssistanceEditor(fiAiAssistanceEditor: HTMLElement) {
        this.tempAiRequestText = getExistingRequestInputEl().innerText;
        fiAiAssistanceEditor.remove();
        this.setIsAIAssistanceVisible(false);
    }

    private _tempAiRequestText: string = undefined;
    set tempAiRequestText(value: string) {
        this._tempAiRequestText = value?.length ? value : undefined;
    }
    get tempAiRequestText(): string {
        return this._tempAiRequestText;
    }

    private showAiRequestEditorInternal(requestMessage?: string) {
        let aiRequestInput: HTMLElement = getExistingRequestInputEl();
        const promise = new Promise<void>((resolve) => {
            if (!!requestMessage?.length && aiRequestInput) {
                aiRequestInput.innerText = requestMessage;
                handleAiRequestInputChange();
                resolve();
            } else {
                const el: HTMLElement = this.textEditorHistoryService.getElement();
                const selection: Selection = document.getSelection();
                const range: Range = this.textEditorInsertMediaService.getRangeForMediaInsert(el, selection);

                const aiRequestEditorEl: HTMLElement = getAiRequestEditorElement({
                    promptSamples: this.promptSamples,
                    promptSamplesTitle: this.appTextService.get('ai.prompt_samples_title'),
                    inputPlaceholder: this.appTextService.get('ai.ask_to_write'),
                    sendRequestFn: (request: string) => this.handleSendMessageToAI(request),
                    requestMessage: requestMessage,
                    clearRequestTooltipText: this.appTextService.get('common.clear'),
                    sendRequestTooltipText: this.appTextService.get('ai.ask_to_write'),
                });

                if (el.children.length) {
                    let currentNode = range.startContainer;
                    while (currentNode && currentNode.parentNode !== el) {
                        currentNode = currentNode.parentNode;
                    }

                    if (currentNode && currentNode.parentNode === el) {
                        if (isDefined(currentNode.nextSibling)) {
                            el.insertBefore(aiRequestEditorEl, currentNode.nextSibling);
                        } else {
                            el.appendChild(aiRequestEditorEl);
                        }

                        range.setStartAfter(aiRequestEditorEl);
                        range.setEndAfter(aiRequestEditorEl);
                        selection.removeAllRanges();
                        selection.addRange(range);
                    } else {
                        range.insertNode(aiRequestEditorEl);
                        range.collapse(false);
                    }
                } else {
                    range.insertNode(aiRequestEditorEl);
                    range.collapse(false);
                }

                setPromptSampleContainerVisibility(!isDefined(requestMessage));
                aiRequestInput = getExistingRequestInputEl();
                setClearRequestButtonVisibility();
                resolve();
            }
        });
        promise.then(() => {
            aiRequestInput.focus();
            this.setIsAIAssistanceVisible(true);
        });
    }

    /*
    getAIResponseElement(aiAssistanceResponse: IFiAIAssistanceResponseModel): HTMLElement {
        if (aiAssistanceResponse?.Choices?.length !== 1) {
            throw new Error(`error in aiAssistanceResponse: ${ JSON.stringify(aiAssistanceResponse) }`)
        }
        const el: HTMLDivElement = document.createElement('div');
        const choice = aiAssistanceResponse.Choices[0];
        el.innerText = choice.Content;
        return el;
    }

    private _showAIAssistanceInput$ = new Subject<void>();
    get showAIAssistanceInput$(): Observable<void> {
        return this._showAIAssistanceInput$.asObservable();
    }
     */

    private _resentRequests$ = new BehaviorSubject<IAIRequestHistoryModel[]>([]);
    resentRequests$: Observable<IAIRequestHistoryModel[]> = this._resentRequests$.asObservable();

    getRecentRequestsCount(): number {
        return this._resentRequests$.value.length;
    }
    setResetRequests(recentRequests: IAIRequestHistoryModel[]) {
        this._resentRequests$.next(recentRequests);
    }

    private handleSendMessageToAI(message: string) {
        this.fiAIAssistanceService
            .sendMessageToAI(message, FiAIAssistanceType.Document)
            .then((aiResponse: IFiAIAssistanceResponseModel) => {
                if (aiResponse?.Choices?.length !== 1) {
                    throw new Error(`error in aiAssistanceResponse: ${JSON.stringify(aiResponse)}`);
                }
                const responseEl: HTMLParagraphElement = document.createElement('div');
                responseEl.innerHTML = aiResponse.Choices[0].Content;
                const aiRequestWrapperEl: HTMLElement = getExistingAiRequestEditorElement();
                aiRequestWrapperEl.replaceWith(responseEl);
                this.getNewRecentRequests();
                this.setIsAIAssistanceVisible(false);
                this.tempAiRequestText = undefined;
            });
    }

    private getNewRecentRequests() {
        this.fiAIAssistanceService
            .getRecentAIRequests(FiAIAssistanceType.Document)
            .then((aiRecentRequests: IAIRequestHistoryModel[]) => {
                this.setResetRequests(aiRecentRequests);
            });
    }
}
